import type { ZodType } from "zod";
import { readConfig } from "../config";
import {
  DocumentsResponseSchema,
  UsersResponseSchema,
  type DocumentsResponse,
  type UsersResponse,
} from "./schemas";

/**
 * The admin-api client. **GET only, on purpose** — this portal has no
 * write/mutate surface anywhere (ISSUE-329 AC), so no POST/PUT/PATCH/DELETE
 * method exists in this module at all; there is nothing to "accidentally"
 * call.
 *
 * The single place a request is issued: base URL from runtime config
 * (`readConfig().apiBaseUrl`), bearer token from the caller's
 * `useAccessToken()` result (`src/auth/useAccessToken.ts` — the only place a
 * token is obtained), non-2xx and shape-mismatched responses both become an
 * `AdminApiError`.
 */

/** Resolves the current operator's access token (`useAccessToken()`'s return type). */
export type AccessTokenGetter = () => Promise<string>;

export class AdminApiError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

async function getJson<Body>(
  path: "/users" | "/documents",
  getAccessToken: AccessTokenGetter,
  schema: ZodType<Body>,
): Promise<Body> {
  const config = readConfig();
  if (!config.ok) {
    throw new AdminApiError("Runtime configuration is missing required fields");
  }
  const token = await getAccessToken();
  const response = await fetch(`${config.config.apiBaseUrl}${path}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    throw new AdminApiError(`Admin API request to ${path} failed`, response.status);
  }
  const body: unknown = await response.json();
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new AdminApiError(`Admin API response from ${path} did not match the expected shape`);
  }
  return parsed.data;
}

export function fetchUsers(getAccessToken: AccessTokenGetter): Promise<UsersResponse> {
  return getJson("/users", getAccessToken, UsersResponseSchema);
}

export function fetchDocuments(getAccessToken: AccessTokenGetter): Promise<DocumentsResponse> {
  return getJson("/documents", getAccessToken, DocumentsResponseSchema);
}
